import { APIModel, DisplayableType } from "../api-model";
import { TransplantCenter } from "../transplant-center";

// TODO: Have more comprehensive types
type UploadedZipDetails = {
  stored_zip_file: any
  error: string
}

type ExtractedFile = {
  file_name: string | null
  path: string | null
  file_type: string | null
  display_name: string | null
  status: string | null
  row_count: number | null
  file_error?: string[]
}

type ExtractedFileDetails = {
  extracted_files: ExtractedFile[]
}

/**
 * Represents a UNOS CSV upload batch, corresponding to the `bk_unos_finalized_offers_csv_batches` database table.
 *
 * A batch tracks the lifecycle of a UNOS CSV zip file upload for a given {@link TransplantCenter}.
 * Each batch is uniquely scoped to a transplant center and batch date, and progresses through
 * a series of statuses as the zip file is uploaded, extracted, staged, processed, and completed.
 *
 * Statuses: `pending` → `uploaded` → `extracting` → `extracted` → `staging` → `staging_completed` → `processing` → `completed` (or `failed`).
 *
 * The extracted CSV files feed into various staging models including ABGs, CBCs, cultures, donor
 * info, donor organs, indicators, infectious diseases, lab panels, lab values, match reviews,
 * offer histories, pump values, recipient outcomes, tests, and urinalyses.
 */
export class FinalizedOffersCSVBatch extends APIModel {
  id: string;
  batch_date: DisplayableType;
  uploaded_zip_details: UploadedZipDetails;
  extracted_file_details: ExtractedFileDetails;
  status: string;
  uploaded_by_id: string | null;
  created_by_id: string | null;
  updated_by_id: string | null;
  created_at: DisplayableType;
  updated_at: DisplayableType;
  transplant_center: TransplantCenter;
  static BatchStatues: {
    [key: string]: {
      text: string,
      className: string
    }
  } = {
      pending: {
        text: 'Pending Upload',
        className: 'bg-secondary text-white'
      },
      uploaded: {
        text: 'Uploaded',
        className: 'bg-info text-white'
      },
      extracting: {
        text: 'Extracting',
        className: 'bg-warning text-dark'
      },
      extracted: {
        text: 'Extracted',
        className: 'bg-primary'
      },
      staging: {
        text: 'Staging',
        className: 'bg-warning text-dark'
      },
      staging_completed: {
        text: 'Staging Complete',
        className: 'bg-success'
      },
      processing: {
        text: 'Processing',
        className: 'bg-secondary'
      },
      completed: {
        text: 'Completed',
        className: 'bg-success'
      },
      failed: {
        text: 'Failed',
        className: 'bg-danger'
      }
    }


  static fieldsToMap = {
    direct: [
      'id',
      'batch_date',
      'zip_file_name',
      'status',
      'uploaded_by_id',
      'created_by_id',
      'updated_by_id'
    ],
    formatDate: [
      'batch_date'
    ],
    formatDateTime: [
      'created_at',
      'updated_at'
    ]
  }

  get batchStatusDisplayText(): string {
    return FinalizedOffersCSVBatch.BatchStatues[this.status] ? FinalizedOffersCSVBatch.BatchStatues[this.status].text : this.status;
  }

  get batchStatusBadgeClass(): string {
    return FinalizedOffersCSVBatch.BatchStatues[this.status] ? FinalizedOffersCSVBatch.BatchStatues[this.status].className : 'bg-secondary';
  }

  get isPending(): boolean {
    return this.status === 'pending';
  }
  
  get isUploaded(): boolean {
    return this.status === 'uploaded';
  }

  get isStaged(): boolean {
    return this.status === 'staging_completed';
  }

  get isExtracting(): boolean {
    return this.status === 'extracting' || this.status === 'extracted' || this.status === 'staging';
  }

  get isProcessing(): boolean {
    return this.status === 'processing';
  }

  get isCompleted(): boolean {
    return this.status === 'completed';
  }

  get isFailed(): boolean {
    return this.status === 'failed';
  }

  get isZipFileReplaceable(): boolean {
    return this.status === 'uploaded' || this.status === 'failed';
  }
  

  static buildFromAPI(record: any): FinalizedOffersCSVBatch {
    const batch: FinalizedOffersCSVBatch = new FinalizedOffersCSVBatch();
    record = record || {};
    batch.transplant_center = TransplantCenter.buildFromAPI(record.transplant_center);
    FinalizedOffersCSVBatch.mapFieldsDirectly<FinalizedOffersCSVBatch>(FinalizedOffersCSVBatch.fieldsToMap.direct, record, batch);
    FinalizedOffersCSVBatch.mapFormatDate<FinalizedOffersCSVBatch>(FinalizedOffersCSVBatch.fieldsToMap.formatDate, record, batch);
    FinalizedOffersCSVBatch.mapFormatDateTime<FinalizedOffersCSVBatch>(FinalizedOffersCSVBatch.fieldsToMap.formatDateTime, record, batch);

    batch.uploaded_zip_details = {
      stored_zip_file: record.uploaded_zip_details?.stored_zip_file,
      error: record.uploaded_zip_details?.error
    };
    batch.extracted_file_details = {
      extracted_files: record.extracted_file_details?.extracted_files
    };

    return batch;
  }
}